import React, { useState } from "react";
import { COLORS, FONTS } from "@/constants/theme";

/**
 * Isotipo de ALTAMA, tomado de brand/altama-logo.svg.
 *
 * @param {{ tamano?: number }} props
 * @returns {JSX.Element}
 */
const MarcaAltama = ({ tamano = 26 }) => (
  <svg aria-hidden="true" height={tamano} viewBox="0 0 128 128" width={tamano}>
    <rect fill="#06184a" height="120" rx="28" width="120" x="4" y="4" />
    <rect
      fill="none"
      height="120"
      rx="28"
      stroke="#0a4ae4"
      strokeOpacity="0.5"
      strokeWidth="3"
      width="120"
      x="4"
      y="4"
    />
    <path
      d="M24 95 64 22l40 73"
      fill="none"
      stroke="#f4f7ff"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="14"
    />
    <path d="M40 67h48" fill="none" stroke="#f4f7ff" strokeLinecap="round" strokeWidth="12" />
    <circle cx="104" cy="103" fill="#0f63ff" r="9.5" />
  </svg>
);

/**
 * Barra de relleno. Hace de contenido de marcador: el mockup enseña
 * estructura, nunca cifras.
 *
 * @param {{ ancho: string, alto?: number, tono?: string }} props
 * @returns {JSX.Element}
 */
const Barra = ({ ancho, alto = 6, tono = "rgba(136,146,204,0.32)" }) => (
  <span
    aria-hidden="true"
    style={{
      display: "block",
      width: ancho,
      height: `${alto}px`,
      borderRadius: "3px",
      backgroundColor: tono,
    }}
  />
);

/**
 * Módulos de la plataforma. Son los de un panel de empresa cualquiera,
 * a propósito genéricos: el mockup ilustra el tipo de producto que ALTAMA
 * construye, no un proyecto concreto.
 */
const MODULOS = [
  { id: "resumen", nombre: "Resumen", titulo: "Resumen general" },
  { id: "kpis", nombre: "KPIs", titulo: "Indicadores clave" },
  { id: "crm", nombre: "CRM", titulo: "Clientes y oportunidades" },
  { id: "reportes", nombre: "Reportes", titulo: "Reportes programados" },
];

/**
 * Vista de "Resumen": una serie temporal y unas filas de actividad.
 *
 * @returns {JSX.Element}
 */
const VistaResumen = () => (
  <>
    <div
      className="relative overflow-hidden rounded-lg border p-3"
      style={{ borderColor: "rgba(0,104,255,0.18)", backgroundColor: "rgba(3,6,26,0.6)" }}
    >
      <svg className="block w-full" height="86" role="presentation" viewBox="0 0 300 86">
        <defs>
          <linearGradient id="ps-area" x1="0" x2="0" y1="0" y2="1">
            <stop offset="0%" stopColor={COLORS.digitalBlue} stopOpacity="0.45" />
            <stop offset="100%" stopColor={COLORS.digitalBlue} stopOpacity="0" />
          </linearGradient>
        </defs>
        {[20, 43, 66].map((y) => (
          <line key={y} stroke="rgba(0,104,255,0.1)" strokeWidth="1" x1="0" x2="300" y1={y} y2={y} />
        ))}
        <path
          d="M0 70 C 30 66, 46 48, 74 51 S 116 31, 142 37 S 188 20, 214 26 S 262 11, 300 16 L300 86 L0 86 Z"
          fill="url(#ps-area)"
        />
        <path
          className="ps-linea"
          d="M0 70 C 30 66, 46 48, 74 51 S 116 31, 142 37 S 188 20, 214 26 S 262 11, 300 16"
          fill="none"
          pathLength="1"
          stroke={COLORS.digitalBlue}
          strokeLinecap="round"
          strokeWidth="2.4"
        />
      </svg>
    </div>

    <div className="mt-3 flex flex-col gap-2.5">
      {[
        { w: "86%", acento: true },
        { w: "72%", acento: false },
        { w: "78%", acento: false },
      ].map((fila, i) => (
        <div
          className="flex items-center gap-2.5 rounded-md px-2.5 py-2"
          key={i}
          style={{ backgroundColor: "rgba(255,255,255,0.025)" }}
        >
          <span
            aria-hidden="true"
            style={{
              width: "14px",
              height: "14px",
              borderRadius: "4px",
              flexShrink: 0,
              backgroundColor: fila.acento ? "rgba(251,139,30,0.75)" : "rgba(0,104,255,0.5)",
            }}
          />
          <Barra ancho={fila.w} />
        </div>
      ))}
    </div>
  </>
);

/**
 * Vista de "KPIs": cuatro medidores y una comparativa en barras.
 *
 * @returns {JSX.Element}
 */
const VistaKpis = () => (
  <>
    <div className="grid grid-cols-2 gap-2.5">
      {[0.82, 0.54, 0.68, 0.39].map((nivel, i) => (
        <div
          className="rounded-lg border p-2.5"
          key={i}
          style={{ borderColor: "rgba(0,104,255,0.18)", backgroundColor: "rgba(3,6,26,0.6)" }}
        >
          <Barra ancho="54%" alto={5} />
          <div
            aria-hidden="true"
            className="mt-2.5 overflow-hidden"
            style={{ height: "5px", borderRadius: "3px", backgroundColor: "rgba(136,146,204,0.16)" }}
          >
            <span
              className="ps-medidor block h-full"
              style={{
                width: `${nivel * 100}%`,
                borderRadius: "3px",
                backgroundColor: i === 3 ? COLORS.webOrange : COLORS.digitalBlue,
                "--medidor-retardo": `${i * 90}ms`,
              }}
            />
          </div>
        </div>
      ))}
    </div>

    <div
      className="mt-3 flex items-end gap-1.5 rounded-lg border p-3"
      style={{ borderColor: "rgba(0,104,255,0.18)", backgroundColor: "rgba(3,6,26,0.6)", height: "74px" }}
    >
      {[38, 52, 30, 64, 46, 72, 58, 80].map((h, i) => (
        <span
          aria-hidden="true"
          className="ps-columna flex-1"
          key={i}
          style={{
            height: `${h}%`,
            borderRadius: "2px",
            backgroundColor: i === 7 ? COLORS.webOrange : "rgba(0,104,255,0.6)",
            "--columna-retardo": `${i * 55}ms`,
          }}
        />
      ))}
    </div>
  </>
);

/**
 * Vista de "CRM": fichas de cliente con su estado.
 *
 * @returns {JSX.Element}
 */
const VistaCrm = () => (
  <div className="flex flex-col gap-2">
    {[
      { ancho: "64%", estado: COLORS.digitalBlue },
      { ancho: "48%", estado: COLORS.webOrange },
      { ancho: "72%", estado: COLORS.digitalBlue },
      { ancho: "56%", estado: "rgba(136,146,204,0.5)" },
      { ancho: "60%", estado: COLORS.digitalBlue },
    ].map((fila, i) => (
      <div
        className="flex items-center gap-2.5 rounded-md border px-2.5 py-2"
        key={i}
        style={{ borderColor: "rgba(0,104,255,0.12)", backgroundColor: "rgba(255,255,255,0.025)" }}
      >
        <span
          aria-hidden="true"
          style={{
            width: "20px",
            height: "20px",
            borderRadius: "50%",
            flexShrink: 0,
            background: "linear-gradient(135deg, #1b2568, #0a4ae4)",
          }}
        />
        <span className="flex flex-1 flex-col gap-1.5">
          <Barra ancho={fila.ancho} alto={5} />
          <Barra ancho="34%" alto={4} tono="rgba(136,146,204,0.2)" />
        </span>
        <span
          aria-hidden="true"
          style={{
            width: "6px",
            height: "6px",
            borderRadius: "50%",
            flexShrink: 0,
            backgroundColor: fila.estado,
          }}
        />
      </div>
    ))}
  </div>
);

/**
 * Vista de "Reportes": documentos con su progreso de generación.
 *
 * @returns {JSX.Element}
 */
const VistaReportes = () => (
  <div className="flex flex-col gap-2.5">
    {[
      { ancho: "70%", progreso: 1 },
      { ancho: "56%", progreso: 1 },
      { ancho: "64%", progreso: 0.45 },
      { ancho: "50%", progreso: 0.15 },
    ].map((fila, i) => (
      <div
        className="rounded-md border px-3 py-2.5"
        key={i}
        style={{ borderColor: "rgba(0,104,255,0.12)", backgroundColor: "rgba(255,255,255,0.025)" }}
      >
        <div className="flex items-center gap-2.5">
          <svg aria-hidden="true" fill="none" height="13" viewBox="0 0 14 16" width="12">
            <path
              d="M2 1h6l4 4v10H2z"
              stroke={fila.progreso === 1 ? COLORS.digitalBlue : "rgba(136,146,204,0.55)"}
              strokeLinejoin="round"
              strokeWidth="1.4"
            />
            <path d="M8 1v4h4" stroke="rgba(136,146,204,0.55)" strokeWidth="1.4" />
          </svg>
          <Barra ancho={fila.ancho} alto={5} />
        </div>
        <div
          aria-hidden="true"
          className="mt-2 overflow-hidden"
          style={{ height: "3px", borderRadius: "2px", backgroundColor: "rgba(136,146,204,0.16)" }}
        >
          <span
            className="ps-medidor block h-full"
            style={{
              width: `${fila.progreso * 100}%`,
              borderRadius: "2px",
              backgroundColor: fila.progreso === 1 ? COLORS.digitalBlue : COLORS.webOrange,
              "--medidor-retardo": `${i * 80}ms`,
            }}
          />
        </div>
      </div>
    ))}
  </div>
);

const VISTAS = {
  resumen: VistaResumen,
  kpis: VistaKpis,
  crm: VistaCrm,
  reportes: VistaReportes,
};

/**
 * Visual del hero: una plataforma de ALTAMA, navegable de verdad.
 *
 * Los módulos son botones: al pulsarlos cambia el panel. Muestra **interfaz,
 * nunca resultados** — no hay una sola cifra, todo son barras de relleno.
 *
 * @returns {JSX.Element}
 */
const PlatformShowcase = () => {
  const [activo, setActivo] = useState("resumen");
  const moduloActivo = MODULOS.find((m) => m.id === activo);
  const Vista = VISTAS[activo];

  return (
    <div className="platform-showcase relative mx-auto w-full max-w-[540px]" style={{ perspective: "1600px" }}>
      <div
        aria-hidden="true"
        className="pointer-events-none absolute"
        style={{
          inset: "-12% -8% -18% -8%",
          background: `radial-gradient(ellipse at 55% 45%, rgba(0,104,255,0.28) 0%, transparent 68%)`,
          filter: "blur(18px)",
        }}
      />

      {/* --- Ventana de escritorio --- */}
      <div
        className="ps-ventana relative overflow-hidden rounded-xl border"
        style={{
          borderColor: "rgba(0,104,255,0.34)",
          background: "linear-gradient(158deg, #0d1240 0%, #070a25 100%)",
          boxShadow: "0 48px 90px rgba(0,0,0,0.55), 0 0 0 1px rgba(255,255,255,0.03) inset",
        }}
      >
        <div
          className="flex items-center gap-3 border-b px-4 py-3"
          style={{ borderColor: "rgba(0,104,255,0.2)", backgroundColor: "rgba(4,8,32,0.7)" }}
        >
          <MarcaAltama tamano={22} />
          <span
            style={{
              color: COLORS.textPrimary,
              fontFamily: FONTS.heading,
              fontSize: "0.76rem",
              fontWeight: 600,
              letterSpacing: "0.06em",
            }}
          >
            ALTAMA
          </span>
          <span
            style={{
              color: COLORS.textMuted,
              fontFamily: FONTS.data,
              fontSize: "0.62rem",
              letterSpacing: "0.12em",
              textTransform: "uppercase",
            }}
          >
            Plataforma
          </span>
          <span className="ml-auto flex items-center gap-1.5" aria-hidden="true">
            {["rgba(136,146,204,0.3)", "rgba(136,146,204,0.3)", COLORS.digitalBlue].map((c, i) => (
              <span key={i} style={{ width: "7px", height: "7px", borderRadius: "50%", backgroundColor: c }} />
            ))}
          </span>
        </div>

        <div className="flex flex-col sm:flex-row">
          {/* Módulos: botones reales, así funcionan igual al tocar.
              En móvil son una fila de pestañas; desde sm, la barra lateral. */}
          <nav
            aria-label="Módulos de la plataforma de ejemplo"
            className="ps-modulos flex shrink-0 gap-1 overflow-x-auto border-b p-2 sm:w-[132px] sm:flex-col sm:overflow-visible sm:border-b-0 sm:border-r sm:p-3"
            style={{ borderColor: "rgba(0,104,255,0.16)" }}
          >
            {MODULOS.map((modulo) => {
              const esActivo = modulo.id === activo;

              return (
                <button
                  aria-pressed={esActivo}
                  className="ps-modulo flex min-h-[44px] shrink-0 items-center gap-2 rounded-md px-3 text-left sm:min-h-0 sm:px-2.5 sm:py-2"
                  key={modulo.id}
                  onClick={() => setActivo(modulo.id)}
                  style={{
                    backgroundColor: esActivo ? "rgba(0,104,255,0.16)" : "transparent",
                    border: `1px solid ${esActivo ? "rgba(0,104,255,0.42)" : "transparent"}`,
                    cursor: "pointer",
                  }}
                  type="button"
                >
                  <span
                    aria-hidden="true"
                    style={{
                      width: "5px",
                      height: "5px",
                      borderRadius: "50%",
                      flexShrink: 0,
                      backgroundColor: esActivo ? COLORS.digitalBlue : "rgba(136,146,204,0.45)",
                    }}
                  />
                  <span
                    style={{
                      color: esActivo ? COLORS.textPrimary : COLORS.textSecondary,
                      fontFamily: FONTS.body,
                      fontSize: "0.72rem",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {modulo.nombre}
                  </span>
                </button>
              );
            })}

            <span className="mt-auto hidden flex-col gap-2 pt-6 sm:flex" aria-hidden="true">
              <Barra ancho="70%" />
              <Barra ancho="52%" />
            </span>
          </nav>

          <div className="min-w-0 flex-1 p-4">
            <div className="mb-4 flex items-center justify-between">
              <span
                style={{
                  color: COLORS.textPrimary,
                  fontFamily: FONTS.heading,
                  fontSize: "0.82rem",
                  fontWeight: 600,
                }}
              >
                {moduloActivo.titulo}
              </span>
              <span
                className="rounded-full px-2.5 py-1"
                style={{
                  border: `1px solid ${COLORS.borderMedium}`,
                  color: COLORS.textSecondary,
                  fontFamily: FONTS.data,
                  fontSize: "0.58rem",
                  letterSpacing: "0.1em",
                  textTransform: "uppercase",
                }}
              >
                En vivo
              </span>
            </div>

            {/* La `key` fuerza el remontaje: cada cambio de módulo reanima la vista. */}
            <div className="ps-vista" key={activo}>
              <Vista />
            </div>
          </div>
        </div>
      </div>

      {/* --- Móvil en primer plano --- */}
      <div
        className="ps-movil absolute hidden overflow-hidden rounded-[20px] border sm:block"
        style={{
          width: "126px",
          left: "-14px",
          bottom: "-34px",
          borderColor: "rgba(0,104,255,0.4)",
          background: "linear-gradient(165deg, #101748 0%, #05081f 100%)",
          boxShadow: "0 30px 60px rgba(0,0,0,0.6)",
        }}
      >
        <div className="flex items-center justify-center py-2" aria-hidden="true">
          <span
            style={{ width: "34px", height: "4px", borderRadius: "3px", backgroundColor: "rgba(136,146,204,0.35)" }}
          />
        </div>

        <div className="px-3 pb-4">
          <div className="mb-3 flex items-center gap-2">
            <MarcaAltama tamano={16} />
            <Barra ancho="46px" alto={5} />
          </div>

          <div
            className="mb-2.5 rounded-lg p-2.5"
            style={{ backgroundColor: "rgba(0,104,255,0.14)", border: "1px solid rgba(0,104,255,0.3)" }}
          >
            <Barra ancho="62%" alto={5} tono="rgba(240,242,255,0.45)" />
            <div className="mt-2 flex items-end gap-1" aria-hidden="true">
              {[14, 22, 11, 27, 19, 30].map((h, i) => (
                <span
                  key={i}
                  style={{
                    width: "7px",
                    height: `${h}px`,
                    borderRadius: "2px",
                    backgroundColor: i === 5 ? COLORS.webOrange : "rgba(0,104,255,0.65)",
                  }}
                />
              ))}
            </div>
          </div>

          <div className="flex flex-col gap-1.5">
            <Barra ancho="88%" alto={5} />
            <Barra ancho="66%" alto={5} />
            <Barra ancho="74%" alto={5} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default PlatformShowcase;
